<template>
    <div :class="classList">
        <v-card
            v-click-outside="onClickOutside"
            class="app-filter-panel__screen d-flex flex-column justify-space-between"
            tile
        >
            <v-layout class="app-filter-panel__screen-header px-6 py-4" align-center>
                <div>
                    <h3 class="text-h3 mt-4">{{ $t('filters') }}</h3>
                    <p v-if="writtenQuery" class="mb-0">{{ writtenQuery }}</p>
                </div>

                <v-spacer />

                <app-button secondary :icon="getIcon('close', 'fas')" :tooltip="$t('close')" @click="hide()" />
            </v-layout>

            <main class="app-filter-panel__screen-main py-4 background darken-1">
                <template v-if="panel">
                    <app-filter-panel-item
                        v-for="(filter, index) in filters"
                        :key="`${filter.column}-${index}`"
                        v-model="filters[index]"
                        :columns="
                            columns.filter(c => {
                                return filter.column === c.value || !filters.find(f => f.column === c.value);
                            })
                        "
                        :option="panel.options.find(o => o.column === filter.column)"
                        :error="errors[filter.column] ? errors[filter.column] : null"
                        @remove-filter="removeFilter(index)"
                        @input="onFilterInput"
                        @input:object="onFilterInputObject(filter.column, $event)"
                    />

                    <app-button
                        class="mx-4 pb-2 pt-4"
                        secondary
                        icon-left="add"
                        :text="$t('add') + ' ' + $t('filter')"
                        @click="addFilter()"
                    />
                </template>
            </main>

            <v-progress-linear :indeterminate="loading" color="primaryBackground" :height="2" />

            <v-card-actions class="app-filter-panel__screen-actions px-6 py-4">
                <app-button
                    :icon-left="getIcon('filter', 'fas')"
                    :text="$t('apply_filters')"
                    :disabled="errors && Object.values(errors).length > 0"
                    @click="applyFilters"
                />

                <transition appear name="fade" mode="out-in">
                    <p v-if="total !== null" class="ml-4 mb-0 caption">
                        {{ $t('found') }} {{ total === null ? 0 : total }}
                        {{ total === 1 ? $t('result').toLowerCase() : $t('results').toLowerCase() }}
                    </p>
                </transition>

                <v-spacer />

                <app-button class="ml-4" secondary :text="$t('remove_filters')" @click="clearFilters" />
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
import { isValidFilter, createFilterQuery } from '@/store/modules/filterPanels';
import getNoCriteriaOperators from '@/lib/helpers/getNoCriteriaOperators';
import parseValidationErrors from '@/lib/helpers/parseValidationErrors';

export default {
    name: 'AppFilterPanel',
    components: {
        AppFilterPanelItem: () => import('@/components/AppFilterPanelItem.vue')
    },
    props: {
        value: {
            type: Boolean,
            default: false,
            required: true
        },
        panelId: {
            type: String,
            required: true
        }
    },
    data: () => ({
        filters: [],
        loading: false,
        total: null,
        items: [],
        onSearch: () => {},
        errors: {}
    }),
    computed: {
        visible: {
            get() {
                return this.value;
            },
            set(v) {
                this.$emit('input', v);
            }
        },
        panel() {
            return this.$store.getters['filterPanels/panel'](this.panelId);
        },
        validFilters() {
            return this.filters.filter(isValidFilter);
        },
        columns() {
            return this.panel ? this.$store.getters['filterPanels/columns'](this.panelId) : [];
        },
        writtenQuery() {
            if (!this.panel) {
                return null;
            }

            const filters = this.validFilters;

            if (filters.length === 0) {
                return null;
            }

            let text = [this.$t('where')];

            filters.forEach((filter, index) => {
                let written = [];

                const option = this.panel.options.find(option => option.column === filter.column);

                if (!option) {
                    return;
                }

                const operator = option.operators.find(op => op.value === filter.operator);

                if (!operator) {
                    return;
                }

                const requiresCriteria = !getNoCriteriaOperators().includes(filter.operator);

                written.push(`${option.label} `);

                if (operator.value === 'in') {
                    written.push(this.$t('is_either').toLowerCase());
                } else if (operator.value === 'not_in') {
                    written.push(this.$t('is_not').toLowerCase());
                } else {
                    written.push(`${operator.text.toLowerCase()}`);
                }

                if (requiresCriteria) {
                    let criteria = filter.criteria;

                    if (Array.isArray(criteria)) {
                        const criteriaString = criteria.map(c => `"${c}"`).join(', ');
                        written.push(criteriaString.replace(/,([^,]*)$/, ' ' + this.$t('or').toLowerCase() + '$1'));
                    } else if (requiresCriteria) {
                        if (filter.data && filter.data.name) {
                            criteria = filter.data.name;
                        }
                        let criteriaText = '';

                        if (option.format === 'money') {
                            criteriaText = `${criteria !== null ? criteria.amount : ''}`;
                        } else if (option.format === 'boolean') {
                            criteriaText = `${criteria ? 'true' : 'false'}`;
                        } else if (option.format === 'date') {
                            criteriaText = this.toDatetime(criteria, 'DD/MM/YYYY');
                        } else if (option.format === 'datetime') {
                            criteriaText = this.toDatetime(criteria, 'DD/MM/YYYY');
                        } else if (option.format === 'enum' && option.enumA11yKey) {
                            criteriaText = `${criteria !== null ? this.$t(option.enumA11yKey + '.' + criteria) : ''}`;
                        } else {
                            criteriaText = `"${criteria !== null ? criteria : ''}"`;
                        }

                        if (option.prependText) {
                            criteriaText = `${option.prependText} ${criteriaText}`;
                        }

                        if (option.appendText) {
                            criteriaText += ` ${option.appendText}`;
                        }

                        written.push(criteriaText);
                    }
                }

                written = written.join(' ');

                if (index === filters.length - 2) {
                    written += ` ${this.$t('and').toLowerCase()}`;
                } else if (index < filters.length - 1) {
                    written += ',';
                }

                text.push(written);
            });

            return text.length > 1 ? text.join(' ') + '.' : null;
        },
        classList() {
            return {
                'app-filter-panel': true,
                'app-filter-panel--visible': this.visible
            };
        }
    },
    watch: {
        'panel.filters': {
            deep: true,
            handler() {
                this.setFiltersArray();
            }
        },
        value: {
            handler(visible) {
                if (visible) {
                    if (this.panel && !this.filters.length) {
                        this.setDefaultFilter();
                    }
                    this.search();
                }
            }
        }
    },
    mounted() {
        this.onSearch = this._.debounce(this.search, 500);
        this.setFiltersArray();
    },
    methods: {
        hide() {
            this.visible = false;
            this.$store.dispatch('filterPanels/hide', this.panelId);
        },
        onClickOutside(e) {
            if (this.visible && e.target.classList.contains('app-filter-panel--visible')) {
                this.hide();
            }
        },
        addFilter(filter = null) {
            filter = filter || {
                column: null,
                operator: null,
                criteria: null,
                condition: 'and'
            };

            this.filters.push(JSON.parse(JSON.stringify(filter)));
        },
        removeFilter(index) {
            this.filters.splice(index, 1);
            this.onSearch();
        },
        setFiltersArray() {
            if (!this.panel) {
                return;
            }

            const filters = Array.isArray(this.panel.filters) ? [...this.panel.filters] : [];

            if (filters.length) {
                this.filters = JSON.parse(JSON.stringify(filters));
            } else {
                this.filters = [];
                this.setDefaultFilter();
            }
        },
        applyFilters() {
            this.$emit('apply-filters', this.validFilters);
            this.hide();
        },
        clearFilters(createDefaultFilter = true, hidePanel = true) {
            this.filters = [];
            this.items = [];
            this.total = null;

            if (createDefaultFilter) {
                this.setDefaultFilter();
            }

            this.$emit('clear-filters');

            if (hidePanel) {
                this.hide();
            }
        },
        setDefaultFilter() {
            if (this.panel && this.columns.find(c => c.value === 'name')) {
                this.addFilter({
                    column: 'name',
                    operator: 'contains',
                    criteria: null,
                    condition: 'and'
                });
            } else {
                this.addFilter();
            }
        },
        onFilterInput(filter = {}) {
            if (isValidFilter(filter)) {
                this.onSearch();
            }
        },
        onFilterInputObject(column, data = null) {
            this.filters = this.filters.map(filter => {
                if (filter.column === column) {
                    filter.data = data;
                }
                return filter;
            });
        },
        async search() {
            const filters = this.validFilters;

            this.total = null;
            this.items = [];
            this.errors = {};

            if (!this.panel || !filters.length) {
                return;
            }

            this.loading = true;

            try {
                const response = await this.panel.requestItems(0, 10, [], createFilterQuery(filters));
                this.items = response.data;

                if (response.metadata && response.metadata.total !== undefined) {
                    this.total = response.metadata.total;
                } else {
                    this.total = response.data.length;
                }
            } catch (error) {
                this.items = [];
                this.total = null;

                let errors = parseValidationErrors(
                    error?.response?.data?.message || error?.response?.data?.data,
                    false
                );

                if (errors.length > 0) {
                    for (const error of errors) {
                        const column = error.field.split('.').find(part => !['query'].includes(part));
                        if (column) {
                            this.errors[`${column}`] = error.message || 'Validation error';
                        }
                    }
                }
            }

            this.loading = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.app-filter-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 5;
    visibility: hidden;

    &:before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1;
        background: #000;
        opacity: 0;
        transition: 0.15s opacity ease-in-out;
    }

    &__screen {
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        width: 100%;
        background: var(--v-background-base);
        z-index: 2;
        transform: translateX(100%);
        transition: 0.15s transform ease-in-out;

        @include respond-above(xs) {
            width: 90%;
        }
        @include respond-above(md) {
            width: 75%;
        }
        @include respond-above(xl) {
            width: 60%;
        }
        @include respond-above(xxl) {
            width: 50%;
        }
    }

    &__screen-header {
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    }

    &__screen-main {
        height: 100%;
        overflow-y: scroll;
    }

    &__screen-actions {
        background: var(--v-primaryLight-base);
    }

    &--visible {
        visibility: visible;
        &:before {
            opacity: 0.4;
        }
        .app-filter-panel__screen {
            transform: translateX(0%);
        }
    }
}
</style>
